import { JwtHighbondClient } from '@acl-services/jwt-highbond-client';
import axios from 'axios';
import React, { useMemo } from 'react';
import {
  AttachmentsApi,
  EntityNamesApi,
  EntityTitlesApi,
  FlattenedIssueTypesApi,
  GovmapGroupsApi,
  GrcObjectsApi,
  IssueActionCountApi,
  IssuesApi,
  IssueSlasApi,
  IssueSubtypesApi,
  IssueTemplatesApi,
  IssueTypesApi,
  MonitoringApi,
  RelationshipsApi,
  SlasApi,
  UsersApi,
} from '../generated/api';
import { ApiContext, ApiContextType } from './api.context';

interface AxiosProviderProps {
  highbondClient: JwtHighbondClient;
  appConfig: {
    refreshUrl: string;
    apiUrl: string;
    loginUrl: string;
  };
  notifyEntitlementError: () => void;
}

export function ApiProvider(
  props: React.PropsWithChildren<AxiosProviderProps>,
) {
  const api = useMemo((): ApiContextType => {
    const axiosClient = axios.create({
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    axiosClient.interceptors.response.use(
      function (response) {
        if (response.data) handleDates(response.data);
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        if (
          !axios.isAxiosError(error) ||
          error.response === undefined ||
          originalRequest._retry
        ) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        const { highbondClient } = props;

        // eslint-disable-next-line no-undef
        const options: RequestInit = {
          method: originalRequest.method.toUpperCase(),
          headers: originalRequest.headers,
          body: originalRequest.data,
          credentials: 'include',
        };

        const response = await highbondClient.fetchWithTokenRefresh(
          originalRequest.url,
          options,
        );

        if (response.ok) {
          // Convert the fetch Response to an Axios-compatible response
          const data = await response.json().catch(() => null);
          return {
            data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            config: originalRequest,
            request: originalRequest,
          };
        }

        const errorResponse = error.response;
        const errorStatus = errorResponse.status;

        if (
          errorStatus === 403 &&
          error.response.data?.code === 'ORGANIZATION_IS_NOT_ENTITLED'
        ) {
          props.notifyEntitlementError();
        }

        return Promise.reject(error);
      },
    );
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { apiUrl } = props.appConfig;
    return {
      axios: axiosClient,
      issuesApi: new IssuesApi(undefined, apiUrl, axiosClient),
      issueTypesApi: new IssueTypesApi(undefined, apiUrl, axiosClient),
      issueSubtypesApi: new IssueSubtypesApi(undefined, apiUrl, axiosClient),
      flattenedIssueTypesApi: new FlattenedIssueTypesApi(
        undefined,
        apiUrl,
        axiosClient,
      ),

      usersApi: new UsersApi(undefined, apiUrl, axiosClient),
      issueTemplatesApi: new IssueTemplatesApi(undefined, apiUrl, axiosClient),
      issueSLAsApi: new IssueSlasApi(undefined, apiUrl, axiosClient),
      issueEntityNamesApi: new EntityNamesApi(undefined, apiUrl, axiosClient),
      issueActionCountApi: new IssueActionCountApi(
        undefined,
        apiUrl,
        axiosClient,
      ),
      slasApi: new SlasApi(undefined, apiUrl, axiosClient),
      govmapGroupsApi: new GovmapGroupsApi(undefined, apiUrl, axiosClient),
      attachmentsApi: new AttachmentsApi(undefined, apiUrl, axiosClient),
      monitoringApi: new MonitoringApi(undefined, apiUrl, axiosClient),
      entityTitlesApi: new EntityTitlesApi(undefined, apiUrl, axiosClient),
      relationshipsApi: new RelationshipsApi(undefined, apiUrl, axiosClient),
      grcObjectsApi: new GrcObjectsApi(undefined, apiUrl, axiosClient),
    };
  }, []);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <ApiContext.Provider value={api}>{props.children}</ApiContext.Provider>
  );
}

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;

function isIsoDateString(value: unknown): value is string {
  return (
    value !== undefined &&
    typeof value === 'string' &&
    isoDateFormat.test(value)
  );
}

function handleDates(body: Record<string, unknown>) {
  if (body === null || body === undefined || typeof body !== 'object') {
    return;
  }

  for (const [key, value] of Object.entries(body)) {
    if (isIsoDateString(value)) {
      // eslint-disable-next-line no-param-reassign
      body[key] = new Date(value);
    } else if (typeof value === 'object') {
      handleDates(value as Record<string, unknown>);
    }
  }
}
